import React, { useEffect, useState, useCallback } from 'react'
import classNames from 'classnames'
import { formatCents, useProject } from 'shared'
import { MatchingEventNotices } from 'ppp'
import { AbTaxNotice, MatchingFundHelper, GtmHelper, findApiV4Link } from 'shared'
import { ProgressBarProps, StatsProps, MatchingFundNoticeProps, ClosedNoticeProps, MatchingFund } from '../stats/types'
import DziSpendensiegelLogo from '../../../assets/images/logos/dzi-spendensiegel.svg'

export const AbStats = ({ apiUrl, matchingEventUrl }: StatsProps): JSX.Element => {
  const { loading, status, ...project } = useProject(apiUrl)
  const [activeMatchingFund, setActiveMatchingFund] = useState<MatchingFund | null>(null)
  useEffect(() => {
    if (loading || !project.id) return
    GtmHelper.pushProductDetailImpression(project)
    const mf = MatchingFundHelper.locallyActiveMatchingFund(project)
    mf && setActiveMatchingFund(mf)
  }, [loading, project])

  const onNewsLinkClicked: React.MouseEventHandler<HTMLAnchorElement> = useCallback((event) => {
    event.preventDefault()
    document.querySelector('#ppp-news-list')?.scrollIntoView({ behavior: 'smooth' })
  }, [])

  return (
    <div
      style={{
        display: 'flex',
        width: '100%',
        justifyContent: 'space-between',
        flexDirection: 'column',
      }}
    >
      <div>
        <div className="flex justify-between items-start mt-4">
          <div className="flex flex-col">
            <strong className="text-purple-600 text-3xl mb-1">{formatCents(project.donated_amount_in_cents)}</strong>
            <span>
              {project.open_amount_in_cents !== 0 && (
                <>von {formatCents(project.donated_amount_in_cents + project.open_amount_in_cents)}</>
              )}{' '}
              gesammelt
            </span>
          </div>
          {project.blog_post_count > 0 && (
            <a
              className="text-right"
              href="#ppp-news-list"
              style={{ marginTop: '0.45rem' }}
              onClick={onNewsLinkClicked}
            >
              {I18n.locale === 'de' ? 'Erfolge und Updates' : 'Updates'}
            </a>
          )}
        </div>
        <ProgressBar percentage={project.progress_percentage} status={status} />
        <div className={classNames('generic-big-numbers is-compressed mb-2', `is-${status}`)}>
          <div className="flex justify-between">
            <div>
              <span className="ab-test-number">{project.progress_percentage} %</span>
              <span>{I18n.t('projects.show.stats.percentage_label')}</span>
            </div>
            <div>
              <span className="ab-test-number">{project.donations_count}</span>
              <span>{I18n.t('projects.show.stats.donations_count_label')}</span>
            </div>
          </div>
        </div>
        <div className="mt-6">
          {status === 'preparing' && <PreparingNotice />}
          {status === 'blocked' && <InactiveNotice />}
          {status === 'closed' && <ClosedNotice {...project.closed_notice} />}
          {status === 'completed' && <CompletedNotice />}
        </div>
      </div>

      {project.donations_prohibited || (status === 'activated' && <AbTaxNotice as="p" className="mb-6" />)}

      {status === 'activated' && (
        <div>
          <MatchingEventNotices project={project} status={status} matchingEventUrl={matchingEventUrl} />
          <MatchingFundNotice activeMatchingFund={activeMatchingFund} />
        </div>
      )}
    </div>
  )
}

/**
 * @info matching fund logo image should be sized 280x72
 */
const MatchingFundNotice = ({ hidden, activeMatchingFund }: MatchingFundNoticeProps): JSX.Element | null => {
  if (hidden || !activeMatchingFund) return null
  return (
    <div className="matching-fund-notice">
      <img src={activeMatchingFund.logo_url} alt={activeMatchingFund.title} />
      <p>{I18n.t('projects.show.stats.matching_fund_notice')}</p>
    </div>
  )
}

const ProgressBar = ({ status, percentage }: ProgressBarProps): JSX.Element | null => {
  return (
    <div className="progress generic-progress-bar mb-4 mt-2">
      <div className="bar" style={{ width: `${percentage}%` }}></div>
    </div>
  )
}

const ClosedNotice = (notice: ClosedNoticeProps): JSX.Element | null => {
  const url = notice && findApiV4Link({ ofObject: notice, withRel: 'call_to_action' })
  if (!url) return null
  return (
    <div className="bpc-notice bpc-notice--alert border border-solid border-yellow-400 mb-4">
      <a href={url} target="_blank" rel="noreferrer">
        {notice?.text}
      </a>
    </div>
  )
}

const InactiveNotice = (): JSX.Element | null => {
  return (
    <div className="bpc-notice bpc-notice--alert border border-solid border-yellow-400 mb-4">
      {I18n.t('projects.show.stats.donate_button_blocked_explanation')}
    </div>
  )
}

const PreparingNotice = (): JSX.Element | null => {
  return (
    <div className="bpc-notice bpc-notice--alert border border-solid border-yellow-400 mb-4">
      {I18n.t('projects.show.stats.donate_button_placeholder')}
    </div>
  )
}

const CompletedNotice = (): JSX.Element | null => {
  return (
    //Todo after AB test: Add notice variant for this case
    <div className="bpc-notice bg-legacy-gray-blue-200 border border-solid border-gray-900 mb-4">
      {I18n.t('projects.show.stats.completed_label')}
    </div>
  )
}
